import { html, nothing } from "lit";
import type { AppViewState } from "./app-view-state.ts";
import { renderTab } from "./app-render.helpers.ts";
import { icons } from "./icons.ts";
import {
  MOBILE_BOTTOM_TABS,
  TAB_GROUPS,
  iconForTab,
  isMobileBottomTab,
  pathForTab,
  shortLabelForTab,
  titleForTab,
  type Tab,
} from "./navigation.ts";

export function renderMobileBottomNav(state: AppViewState) {
  const moreActive = state.mobileNavMoreOpen || !isMobileBottomTab(state.tab);

  return html`
    <nav class="mobile-bottom-nav" aria-label="Primary navigation">
      ${MOBILE_BOTTOM_TABS.map((tab) => renderMobileBottomTab(state, tab))}
      <button
        type="button"
        class="mobile-bottom-nav__item ${moreActive ? "active" : ""}"
        aria-expanded=${state.mobileNavMoreOpen}
        aria-haspopup="dialog"
        @click=${() => {
          state.mobileNavMoreOpen = !state.mobileNavMoreOpen;
        }}
        title="All sections"
      >
        <span class="mobile-bottom-nav__icon" aria-hidden="true">${icons.menu}</span>
        <span class="mobile-bottom-nav__label">More</span>
      </button>
    </nav>
    ${state.mobileNavMoreOpen ? renderMobileNavSheet(state) : nothing}
  `;
}

function renderMobileBottomTab(state: AppViewState, tab: Tab) {
  const href = pathForTab(tab, state.basePath);
  const active = state.tab === tab && !state.mobileNavMoreOpen;

  return html`
    <a
      href=${href}
      class="mobile-bottom-nav__item ${active ? "active" : ""}"
      @click=${(event: MouseEvent) => {
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        event.preventDefault();
        state.mobileNavMoreOpen = false;
        state.setTab(tab);
      }}
      title=${titleForTab(tab)}
      aria-current=${active ? "page" : nothing}
    >
      <span class="mobile-bottom-nav__icon" aria-hidden="true">${icons[iconForTab(tab)]}</span>
      <span class="mobile-bottom-nav__label">${shortLabelForTab(tab)}</span>
    </a>
  `;
}

function renderMobileNavSheet(state: AppViewState) {
  return html`
    <div class="mobile-nav-backdrop" @click=${() => (state.mobileNavMoreOpen = false)}></div>
    <div
      class="mobile-nav-sheet"
      role="dialog"
      aria-modal="true"
      aria-label="All Jaisiu sections"
    >
      <div class="mobile-nav-sheet__header">
        <div class="mobile-nav-sheet__title">All sections</div>
        <button
          type="button"
          class="btn btn--sm"
          @click=${() => (state.mobileNavMoreOpen = false)}
          aria-label="Close"
        >
          Close
        </button>
      </div>
      <div class="mobile-nav-sheet__body">
        ${TAB_GROUPS.map(
          (group) => html`
            <section class="mobile-nav-sheet__group">
              <div class="mobile-nav-sheet__group-label">${group.label}</div>
              <div class="mobile-nav-sheet__items">
                ${group.tabs.map((tab) => renderTab(state, tab))}
              </div>
            </section>
          `,
        )}
        <section class="mobile-nav-sheet__group">
          <div class="mobile-nav-sheet__group-label">Resources</div>
          <div class="mobile-nav-sheet__items">
            <a
              class="nav-item nav-item--external"
              href="https://pryzm.at"
              target="_blank"
              rel="noreferrer"
            >
              <span class="nav-item__icon" aria-hidden="true">${icons.book}</span>
              <span class="nav-item__text">Website</span>
            </a>
          </div>
        </section>
      </div>
    </div>
  `;
}
